import React, { useCallback, useEffect, useRef, useState } from "react"
import { observer } from "mobx-react-lite"
import { Loader, MultiSelect, type MultiSelectProps, type SelectItem } from "@mantine/core"
import { InstructorStore } from "../../../app/store"
import { SelectorElement } from "./SelectorElement"
import { SelectorValueComponent } from "./SelectorValueComponent"
import { type Instructor } from "../../../shared/types/instructor"

type InstructorSelectorProps = Omit<MultiSelectProps, "data"> & React.RefAttributes<HTMLInputElement>

export const InstructorSelectorMulti = observer(function InstructorSelectorMulti(props: InstructorSelectorProps) {
  const { isLoading, getAll, instructors } = InstructorStore
  const [ data, setData ] = useState<Array<SelectItem>>(instructors.map(valueToEntity))
  const [ isInView, setIsInView ] = useState(false)
  const selectRef = useRef<HTMLDivElement>(null)

  const onOpen = useCallback(() => {
    if (instructors.length === 0 && !isLoading) {
      void getAll({ publishedOnly: false })
    }
  }, [ instructors.length, isLoading, getAll ])

  useEffect(() => {
    setData(instructors.map(valueToEntity))
  }, [ instructors ])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsInView(true)
        }
      },
      {
        threshold: 0.1,
      },
    )

    if (selectRef.current) {
      observer.observe(selectRef.current)
    }

    return () => {
      if (selectRef.current) {
        observer.unobserve(selectRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (isInView && data.length === 0 && !isLoading) {
      void getAll()
    }
  }, [ isInView, data.length, isLoading, getAll ])

  return (
    <div ref={selectRef}>
      <MultiSelect
        radius="md"
        label="Выберите преподавателей"
        description="Эти преподаватели будут отображаться в команде, которая ведет данный курс"
        placeholder="Выберите преподавателей"
        nothingFound="Никто не найден"
        itemComponent={SelectorElement}
        valueComponent={SelectorValueComponent}
        searchable
        maxDropdownHeight={400}
        onDropdownOpen={onOpen}
        rightSection={isLoading ? <Loader size={16}/> : null}
        data={data}
        {...props}
      />
    </div>
  )
})

function valueToEntity(instructor: Instructor): SelectItem {
  return {
    ...instructor,
    value: instructor._id,
    label: instructor.name,
  }
}
